/**
 * The LLM half of the rival.
 *
 * It only ever supplies a goal list; RivalAgent executes it. That boundary is
 * what makes latency survivable: a plan takes seconds to arrive and describes
 * seconds of work, while the executor keeps steering at 60fps the whole time.
 * Every failure path lands on the agent's own heuristic planner, so the rival
 * degrades from "clever" to "competent" rather than from "racing" to "stood
 * still".
 */
export class RivalBrain {
  constructor({ agent, bus, endpoint = '/api/rival' }) {
    this.agent = agent;
    this.bus = bus;
    this.endpoint = endpoint;

    this.available = false;      // set by probe()
    this.enabled = true;
    this.interval = 4.0;         // seconds between plan requests
    // Generous: routed reasoning models have been measured at 20s+ per plan.
    // The executor races on regardless, so a slow plan costs nothing but
    // freshness, while an abort throws the whole reply away.
    this.timeoutMs = 45000;

    this._cooldown = 1.5;
    this._inFlight = false;
    this._failures = 0;
    this._abort = null;
    this.lastError = null;
    this.plansServed = 0;
  }

  /** Ask the Worker whether a rival brain is configured at all. */
  async probe() {
    try {
      const res = await fetch('/api/health', { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(`health ${res.status}`);
      const body = await res.json();
      this.available = body?.rival === true;
    } catch (e) {
      this.available = false;
      this.lastError = String(e?.message ?? e);
    }
    return this.available;
  }

  reset() {
    this._cooldown = 1.5;
    this._failures = 0;
    this.plansServed = 0;
    this.lastError = null;
    this.abort();
  }

  abort() {
    if (this._abort) { this._abort.abort(); this._abort = null; }
    this._inFlight = false;
  }

  /**
   * @param {number} dt
   * @param {{playerPosition: THREE.Vector3, mode: string}} ctx
   */
  update(dt, ctx) {
    if (!this.enabled || !this.available || this._inFlight) return;
    // A baked route is already the answer to "where next": spend no tokens
    // narrating it. The brain re-engages the moment the route runs out,
    // and at branch points, where the route deliberately declines to
    // choose. That is the judgement call worth a network round trip: the
    // executor knows both lines, only the brain knows the race.
    if (this.agent.branch && !this._branchAsked) {
      this._branchAsked = this.agent.branch.at;
      this._decide(ctx);
      return;
    }
    if (this.agent.routeActive) return;
    this._branchAsked = null;
    this._cooldown -= dt;
    if (this._cooldown > 0) return;
    // Only spend a call when the agent is actually running dry, and never
    // while an earlier answer is still queued for its goal boundary: that
    // would just overwrite advice nobody has read yet.
    if (this.agent.goals.length > 1 || this.agent.pendingPlan) return;

    this._cooldown = this.interval;
    this._request(ctx);
  }

  /**
   * The branch decision. Deliberately a different shape from a plan: a
   * one-word answer against a tiny state, so it can be asked with a short
   * deadline and thrown away without cost. The executor takes the safe
   * line on its own if this does not land in time.
   */
  async _decide(ctx) {
    const branch = this.agent.branch;
    if (!branch) return;
    this._inFlight = true;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 2200);
    try {
      const res = await fetch('/api/rival/branch', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          why: branch.why,
          gapMetres: Math.round(ctx.gapMetres ?? 0),
          raceSeconds: Math.round(ctx.raceSeconds ?? 0),
          leading: !!ctx.leading,
        }),
        signal: ctl.signal,
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.ok) return;
      if (this.agent.branch?.at === branch.at) this.agent.pick(body.take);
    } catch {
      /* the safe line is already the default */
    } finally {
      clearTimeout(timer);
      this._inFlight = false;
    }
  }

  async _request(ctx) {
    this._inFlight = true;
    this._abort = new AbortController();
    const timer = setTimeout(() => this._abort?.abort(), this.timeoutMs);

    try {
      const res = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(this.agent.describeState(ctx.playerPosition, ctx.mode)),
        signal: this._abort.signal,
      });
      const body = await res.json().catch(() => null);

      // 429 means the Worker's rate limit, not a broken endpoint: settle into
      // a slower cadence instead of counting toward the give-up threshold.
      if (res.status === 429) {
        this._cooldown = Math.max(this.interval * 4, 30);
        return;
      }
      if (!res.ok || !body?.ok) {
        throw new Error(body?.reason || body?.error || `http ${res.status}`);
      }

      const accepted = this.agent.setPlan(body.plan);
      if (accepted) {
        this.plansServed++;
        this._failures = 0;
        this.lastError = null;
        if (body.say) this.bus.emit('rival:say', { text: String(body.say).slice(0, 160) });
      }
    } catch (e) {
      this._failures++;
      this.lastError = e?.name === 'AbortError' ? 'timeout' : String(e?.message ?? e);
      // Back off hard rather than hammering a broken endpoint mid-race.
      this._cooldown = Math.min(60, this.interval * 2 ** this._failures);
      if (this._failures >= 4) {
        this.available = false;
        this.bus.emit('rival:offline', { reason: this.lastError });
      }
    } finally {
      clearTimeout(timer);
      this._inFlight = false;
      this._abort = null;
    }
  }
}
